// ─────────────────────────────────────────────────────────────────────────────
// Settings (gear) data layer, 1:1 port of the legacy Settings drawer wiring:
//   • Artist Profile : site/js/settings-wiring.js  (band_profile blob + validate)
//   • Connections    : site/js/connectors-wiring.js (connections blob + OAuth)
//   • Account        : settings-wiring.js change-password + window.ac.user paint
//
// Worker contracts (verified in cntrl-auth-worker.js + STATS_WORKER):
//   GET/PUT /api/data/band_profile        → { success, data }  (profile blob)
//   GET/PUT /api/data/connections         → { success, data }  (connections blob)
//   GET     STATS /validate-artist?name=  → { found, monthly_listeners, followers, name }
//   POST    /api/change-password { currentPassword, newPassword } → { token } | { error }
//   PUT     /api/me/profile { open_to_support }                   → 200 | error
//   GET     /api/google-calendar/status   → { connected, email }
//   POST    /api/google-calendar/auth     → { authUrl }
//   POST    /api/google-calendar/sync     → { success, created, updated }
//   POST    /api/google-calendar/disconnect
//
// GET sends Bearer-only (apiGet → authHeaders('GET')); PUT/POST send Bearer +
// Content-Type (apiPut/apiPost). Connections read reuses fetchConnections()'s
// defensive unwrap from lib/dashboard.
// ─────────────────────────────────────────────────────────────────────────────

import { apiPut, AUTH_WORKER, STATS_WORKER } from './api';
import { getToken, getCurrentBandId, authHeaders } from './auth';
import { getCachedUser } from './dashboard';
import { fetchConnections } from './dashboard';
import type { Connections, User } from './types';

// ─── Artist Profile (band_profile blob, mirrors settings-wiring.js) ───────────

export type ArtistChipStatus = 'green' | 'yellow' | 'red' | 'validating';

export interface ComparableArtist {
  name: string;
  listeners: number | null;
  status: ArtistChipStatus;
}

export interface BandProfile {
  artists: ComparableArtist[];
  sound?: string;
  audience?: string;
  goals?: string;
  working?: string;
  voice?: string;
  avoid?: string;
}

export const PROFILE_TEXT_FIELDS = [
  { key: 'sound', label: 'Describe your sound', hint: 'In one sentence, what do you actually sound like?', placeholder: 'e.g. Indie rock with shoegaze influences', multiline: false },
  { key: 'audience', label: 'Target audience', hint: "Who's your ideal fan? Age, vibe, where they hang out.", placeholder: 'Who is your music for? Age range, scene, vibe…', multiline: true },
  { key: 'goals', label: 'Current goals', hint: 'What are you trying to accomplish right now?', placeholder: 'What are you working toward this year?', multiline: true },
  { key: 'working', label: "What's working", hint: 'What content has performed best for you so far?', placeholder: 'What posts/content are getting traction lately?', multiline: true },
  { key: 'voice', label: 'Brand voice', hint: 'Your tone and personality on social media.', placeholder: 'e.g. Authentic, dry humor, no marketing speak', multiline: false },
  { key: 'avoid', label: 'Things to avoid', hint: 'What you never want to sound like or be associated with.', placeholder: "Topics, styles, or messaging you don't want.", multiline: true },
] as const;

/** Per-artist localStorage key (mirrors _bpKey() in settings-wiring.js).
    profile-leak-fix 2026-07-16 (found live at the Relient K onboarding): the
    'self' fallback was GLOBAL across accounts on the same browser, Whitsett's
    cached profile painted into a fresh Relient K login and one Save would have
    written it to RK's cloud profile. Now scoped by the signed-in account
    (getCachedUser().id, warm from /api/me) with the shared legacy key only as
    a last resort during the first paint of a session. */
function bpKey(): string | null {
  try {
    const bid = getCurrentBandId() || '';
    if (bid) return 'ac_band_profile_' + bid;
    const uid = (getCachedUser() as { id?: string } | undefined)?.id || '';
    /* No resolvable account scope → NO shared fallback key. Reading the old
       global 'self' key is exactly the leak; skipping the instant-paint for
       one render is the safe trade (cloud lands right after). */
    return uid ? 'ac_band_profile_u_' + uid : null;
  } catch {
    return null;
  }
}

/** Read the cached profile from localStorage (instant paint before cloud lands). */
export function getProfileLocal(): BandProfile {
  try {
    const k = bpKey();
    if (!k) return { artists: [] }; // profile-leak-fix: no account scope → no cache read
    const p = JSON.parse(localStorage.getItem(k) || '{}') as BandProfile;
    if (!Array.isArray(p.artists)) p.artists = [];
    return p;
  } catch {
    return { artists: [] };
  }
}

/** Persist locally + push to the cloud (PUT /api/data/band_profile). */
export async function saveProfile(p: BandProfile): Promise<boolean> {
  try {
    const k = bpKey();
    if (k) localStorage.setItem(k, JSON.stringify(p));
    /* profile-leak-fix: retire the legacy shared key so it can never paint
       into another account on this browser again. */
    localStorage.removeItem('ac_band_profile_self');
  } catch {
    /* ignore quota errors */
  }
  if (!getToken()) return true;
  try {
    await apiPut('auth', '/api/data/band_profile', p);
    return true;
  } catch {
    return false;
  }
}

interface CloudProfileResponse {
  readonly success?: boolean;
  readonly data?: BandProfile | string;
}

/** Pull the canonical profile from D1; cloud wins when it has content. */
export async function fetchCloudProfile(): Promise<BandProfile | null> {
  if (!getToken()) return null;
  try {
    // GET via fetch directly (apiGet would 404-throw); mirror the legacy read.
    // Must carry X-Band-Id (authHeaders), band_profile is band-scoped, so under
    // roster impersonation a bearer-only read returned the CALLER's profile while
    // saveProfile (scoped) wrote to the active artist → mismatched read/write.
    const res = await fetch(AUTH_WORKER + '/api/data/band_profile', {
      headers: authHeaders('GET'),
    });
    if (!res.ok) return null;
    const resp = (await res.json()) as CloudProfileResponse;
    if (!resp || !resp.success) return null;
    let d = resp.data;
    if (typeof d === 'string') {
      try {
        d = JSON.parse(d) as BandProfile;
      } catch {
        return null;
      }
    }
    return (d as BandProfile) || null;
  } catch {
    return null;
  }
}

/** True when a cloud profile actually has content worth overwriting local. */
export function profileHasContent(p: BandProfile | null): boolean {
  if (!p) return false;
  return Boolean(
    (p.artists && p.artists.length) || p.sound || p.audience || p.goals || p.working || p.voice || p.avoid,
  );
}

export function fmtListeners(n: number | null | undefined): string {
  const num = Number(n);
  if (!isFinite(num) || num <= 0) return '';
  if (num >= 1e6) return (num / 1e6).toFixed(1).replace(/\.0$/, '') + 'M listeners';
  if (num >= 1e3) return Math.round(num / 100) / 10 + 'K listeners';
  return num + ' listeners';
}

interface ValidateArtistResponse {
  readonly found?: boolean;
  readonly error?: string;
  readonly monthly_listeners?: number;
  readonly followers?: number;
  readonly name?: string;
}

/** Validate a comparable artist against the stats worker; tiers green/yellow/red.
 *  Mirrors addComparableArtist()'s fetch + thresholds (settings-wiring.js:146). */
export async function validateArtist(
  name: string,
): Promise<{ listeners: number; status: ArtistChipStatus; name?: string } | null> {
  try {
    const r = await fetch(STATS_WORKER + '/validate-artist?name=' + encodeURIComponent(name));
    if (!r.ok) return { listeners: 0, status: 'yellow' };
    const data = (await r.json()) as ValidateArtistResponse;
    if (!data || data.error || !data.found) return { listeners: 0, status: 'red' };
    const listeners = data.monthly_listeners || data.followers || 0;
    const status: ArtistChipStatus = listeners >= 50000 ? 'green' : listeners >= 5000 ? 'yellow' : 'red';
    return { listeners, status, name: data.name };
  } catch {
    return { listeners: 0, status: 'yellow' };
  }
}

// ─── Account (change password + open-to-support, mirrors settings-wiring.js) ──

interface ChangePasswordResponse {
  readonly token?: string;
  readonly error?: string;
  readonly message?: string;
}

export interface ChangePasswordResult {
  readonly ok: boolean;
  readonly message: string;
}

/** POST /api/change-password, worker invalidates all sessions, returns a fresh
 *  token for THIS device which we persist (settings-wiring.js:671). */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<ChangePasswordResult> {
  if (!getToken()) return { ok: false, message: "You're not logged in." };
  try {
    const res = await fetch(AUTH_WORKER + '/api/change-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + getToken() },
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    const data = (await res.json().catch(() => null)) as ChangePasswordResponse | null;
    if (!res.ok || (data && data.error)) {
      return { ok: false, message: (data && (data.error || data.message)) || 'Could not update password.' };
    }
    if (data && data.token) {
      try {
        localStorage.setItem('cntrl_auth_token', data.token);
      } catch {
        /* ignore */
      }
    }
    return { ok: true, message: 'Password updated.' };
  } catch {
    return { ok: false, message: 'Network error, try again.' };
  }
}

/** PUT /api/me/profile { open_to_support }, user-level flag (settings-wiring.js:570). */
export async function saveOpenToSupport(open: boolean): Promise<boolean> {
  if (!getToken()) return false;
  try {
    await apiPut('auth', '/api/me/profile', { open_to_support: open });
    return true;
  } catch {
    return false;
  }
}

/** Account display helpers (paintAccount, settings-wiring.js:297). */
export function planLabel(user: User | null): string {
  /* shopify-1.2.2 round 2 (2026-07-10): the 'Lifetime' branch is GONE, a
     static Lifetime badge reads as a purchasable plan with disabled billing
     to Shopify review. Comped accounts simply show no plan UI (the Settings
     gate never renders this for them anyway). */
  /* shopify-billing 2026-07-09, show the real shape of the subscription:
     cadence rides /api/me now. Unknown cadence falls back to plain 'AC'. */
  /* acp 2026-07-24 — Pro rows label as ACP regardless of cadence (pricing
     TBD, so no $ suffix yet — add it when stripe_acp_monthly lands). */
  if (String(user?.plan_tier || '').toLowerCase() === 'acp') return 'Artist CNTRL Pro';
  const cad = String(user?.billing_cadence || '').toLowerCase();
  if (cad === 'annual') return 'AC · $120/mo · billed annually';
  if (cad === 'monthly') return 'AC · $150/mo · month-to-month';
  return 'AC';
}
export function memberSince(user: User | null): string {
  const created = user?.created_at as string | undefined;
  if (!created) return '-';
  const d = new Date(created);
  if (isNaN(d.getTime())) return '-';
  return d.getMonth() + 1 + '/' + d.getDate() + '/' + d.getFullYear();
}

// ─── Personal profile (My profile editor, mirrors site/m/profile.html) ────────
//
// The legacy mobile "My profile" page reads the signed-in user's PERSONAL profile
// (display name, phone, location, bio, avatar, skills, travel info) and writes it
// back. This is explicitly NOT band-scoped, even under impersonation the
// endpoints target the signed-in caller (the worker ignores X-Band-Id here):
//   GET  /api/users/{id}/profile  → { profile } | profile   (nested `travel` blob)
//   PUT  /api/me/profile { display_name, phone, location, bio, avatar_url,
//                          skills, travel_* }                (flat travel fields)

export interface LoyaltyRow {
  program: string;
  number: string;
}

/** The personal-profile edit buffer (mirrors profile.html state.profile). */
export interface PersonalProfile {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  location: string;
  bio: string;
  avatar_url: string;
  skills: string[];
  travel_known_traveler_number: string;
  travel_redress_number: string;
  travel_airline_loyalty: LoyaltyRow[];
  travel_hotel_loyalty: LoyaltyRow[];
}

/** Split a display name into first / rest (mirrors profile.html splitName). */
export function splitDisplayName(displayName: string): { first: string; last: string } {
  const dn = String(displayName || '')
    .trim()
    .replace(/\s+/g, ' ');
  const idx = dn.indexOf(' ');
  return {
    first: idx === -1 ? dn : dn.slice(0, idx),
    last: idx === -1 ? '' : dn.slice(idx + 1),
  };
}

interface RawProfileResponse {
  readonly profile?: RawProfile;
  readonly display_name?: string;
  readonly phone?: string;
  readonly location?: string;
  readonly bio?: string;
  readonly avatar_url?: string;
  readonly email?: string;
  readonly skills?: unknown;
  readonly travel?: RawTravel;
}
interface RawProfile {
  readonly display_name?: string;
  readonly phone?: string;
  readonly location?: string;
  readonly bio?: string;
  readonly avatar_url?: string;
  readonly email?: string;
  readonly skills?: unknown;
  readonly travel?: RawTravel;
}
interface RawTravel {
  readonly known_traveler_number?: string;
  readonly redress_number?: string;
  readonly airline_loyalty?: ReadonlyArray<{ program?: string; airline?: string; number?: string }>;
  readonly hotel_loyalty?: ReadonlyArray<{ program?: string; chain?: string; number?: string }>;
}

/**
 * GET /api/users/{id}/profile, the caller's personal profile.
 * Mirrors profile.html load(): the worker nests travel under a `travel` object on
 * the read path. Returns null on any failure so the screen can fall back to the
 * /api/me-derived shell. NOT band-scoped (Bearer-only).
 */
export async function fetchPersonalProfile(
  userId: string | number,
): Promise<Partial<PersonalProfile> | null> {
  if (!getToken()) return null;
  try {
    const res = await fetch(
      AUTH_WORKER + '/api/users/' + encodeURIComponent(String(userId)) + '/profile',
      { headers: { Authorization: 'Bearer ' + getToken() } },
    );
    if (!res.ok) return null;
    const data = (await res.json()) as RawProfileResponse | null;
    if (!data) return null;
    const p: RawProfile = (data.profile || data) as RawProfile;
    const name = splitDisplayName(p.display_name || '');
    const travel: RawTravel = p.travel || {};
    return {
      first_name: name.first,
      last_name: name.last,
      phone: p.phone || '',
      location: p.location || '',
      bio: p.bio || '',
      avatar_url: p.avatar_url || '',
      email: p.email || '',
      skills: Array.isArray(p.skills) ? (p.skills as string[]).slice() : [],
      travel_known_traveler_number: travel.known_traveler_number || '',
      travel_redress_number: travel.redress_number || '',
      travel_airline_loyalty: Array.isArray(travel.airline_loyalty)
        ? travel.airline_loyalty.map((r) => ({
            program: String(r.program || r.airline || ''),
            number: String(r.number || ''),
          }))
        : [],
      travel_hotel_loyalty: Array.isArray(travel.hotel_loyalty)
        ? travel.hotel_loyalty.map((r) => ({
            program: String(r.program || r.chain || ''),
            number: String(r.number || ''),
          }))
        : [],
    };
  } catch {
    return null;
  }
}

/**
 * PUT /api/me/profile, writes the CALLER's personal profile (display_name,
 * avatar, travel info, …). Explicitly NOT band-scoped; even when impersonating,
 * this targets the signed-in user (the worker ignores X-Band-Id for /api/me).
 * Mirrors profile.html save(): blank loyalty rows are stripped, skills capped at
 * 30 (the worker also enforces both). Reuses apiPut (Bearer + Content-Type).
 */
export async function savePersonalProfile(p: PersonalProfile): Promise<boolean> {
  if (!getToken()) return false;
  const clean = (rows: LoyaltyRow[]): LoyaltyRow[] =>
    (rows || []).filter((r) => r && (r.program || '').trim() && (r.number || '').trim());
  const body = {
    display_name: (p.first_name + ' ' + p.last_name).trim(),
    phone: p.phone,
    location: p.location,
    bio: p.bio,
    avatar_url: p.avatar_url,
    skills: (p.skills || []).slice(0, 30),
    travel_known_traveler_number: p.travel_known_traveler_number,
    travel_redress_number: p.travel_redress_number,
    travel_airline_loyalty: clean(p.travel_airline_loyalty),
    travel_hotel_loyalty: clean(p.travel_hotel_loyalty),
  };
  try {
    await apiPut('auth', '/api/me/profile', body);
    return true;
  } catch {
    return false;
  }
}

// ─── Connections (connections blob + OAuth, mirrors connectors-wiring.js) ──────

/** The mutable connections blob, platform → arbitrary credential sub-object. */
export type ConnBlob = Record<string, Record<string, unknown> | undefined>;

/** GET /api/data/connections (reuses fetchConnections's defensive unwrap). */
export async function fetchConnBlob(): Promise<ConnBlob> {
  const c = (await fetchConnections()) as Connections;
  return (c as unknown as ConnBlob) || {};
}

/** PUT /api/data/connections, server is source of truth (connectors-wiring.js:76). */
export async function saveConnBlob(c: ConnBlob): Promise<boolean> {
  if (!getToken()) return false;
  try {
    await apiPut('auth', '/api/data/connections', c);
    return true;
  } catch {
    return false;
  }
}

/* fourthwall 2026-07-18, credential connect (no OAuth; Fourthwall's Open API
   uses a Basic-auth username/password pair from the shop dashboard →
   Settings → For developers → Open API). The worker validates the pair LIVE
   against /order before persisting, applies the merch-channel interlock
   (Fourthwall is online-only, Shopify/Square get moved to POS), and encrypts
   the password at rest. Returns the worker's error string so the drawer can
   show "rejected these credentials" verbatim. */
export async function fourthwallConnect(
  apiUser: string,
  apiKey: string,
): Promise<{ ok: boolean; error?: string }> {
  const t = getToken();
  if (!t) return { ok: false, error: 'Not signed in.' };
  try {
    const res = await fetch(AUTH_WORKER + '/api/fourthwall/connect', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + t, 'Content-Type': 'application/json' },
      body: JSON.stringify({ apiUser: apiUser.trim(), apiKey }),
    });
    const data = (await res.json().catch(() => null)) as { success?: boolean; error?: string } | null;
    if (res.ok && data && data.success) return { ok: true };
    return { ok: false, error: (data && data.error) || 'Could not connect Fourthwall.' };
  } catch {
    return { ok: false, error: 'Could not reach the server, try again.' };
  }
}

// Parsers (verbatim from connectors-wiring.js:145-172).
export function parseSpotifyId(input: string): string {
  if (!input) return '';
  const s = String(input).trim();
  let m = s.match(/spotify\.com\/artist\/([A-Za-z0-9]+)/);
  if (m) return m[1];
  m = s.match(/spotify:artist:([A-Za-z0-9]+)/);
  if (m) return m[1];
  if (/^[A-Za-z0-9]{20,30}$/.test(s)) return s;
  return '';
}
export function parseYouTube(input: string): string {
  if (!input) return '';
  const s = String(input).trim();
  let m = s.match(/youtube\.com\/(?:channel\/)?(UC[A-Za-z0-9_-]{20,})/);
  if (m) return m[1];
  m = s.match(/youtube\.com\/@([A-Za-z0-9_.-]+)/);
  if (m) return '@' + m[1];
  if (s.charAt(0) === '@') return s;
  if (/^UC[A-Za-z0-9_-]{20,}$/.test(s)) return s;
  return '';
}
export function parseBandsintown(input: string): string {
  if (!input) return '';
  const s = String(input).trim();
  const m = s.match(/bandsintown\.com\/a?\/?([^?/]+)/i);
  if (m && m[1] !== 'a') return decodeURIComponent(m[1]);
  return s;
}

/** A connector's derived UI state (mirrors renderConnectionStatus paint). */
export interface ConnView {
  connected: boolean;
  label: string; // pill text ("Online" / "Connected" / "Not connected")
  variant: '' | 'success' | 'warning' | 'muted';
  meta: string; // the conn-card__meta line
}

const NOT_CONNECTED: ConnView = { connected: false, label: 'Not connected', variant: '', meta: '' };

/** Derive each platform's pill + meta from the blob (renderConnectionStatus). */
export function connView(c: ConnBlob, platform: string): ConnView {
  const get = (k: string): Record<string, unknown> => (c[k] || {}) as Record<string, unknown>;
  switch (platform) {
    case 'spotify': {
      const s = get('spotify');
      return s.id
        ? { connected: true, label: 'Online', variant: 'success', meta: 'Spotify Artist · ' + s.id }
        : NOT_CONNECTED;
    }
    case 'youtube': {
      const y = get('youtube');
      return y.id
        ? { connected: true, label: 'Online', variant: 'success', meta: String(y.id) }
        : NOT_CONNECTED;
    }
    case 'meta': {
      const m = get('meta');
      if (m.pageId || m.igUserId) {
        const bits: string[] = [];
        if (m.pageId || m.pageName) bits.push('Page: ' + (m.pageName || m.pageId));
        if (m.igUserId) bits.push(m.igUsername ? 'Instagram: @' + m.igUsername : 'Instagram connected');
        return { connected: true, label: 'Online', variant: 'success', meta: bits.join(' · ') };
      }
      return NOT_CONNECTED;
    }
    case 'shopify': {
      const sh = get('shopify');
      return sh.domain
        ? { connected: true, label: 'Online', variant: 'success', meta: String(sh.domain) }
        : NOT_CONNECTED;
    }
    case 'square': {
      // audit 2026-07-02: the OAuth callback stores {merchantId, token,
      // businessName}, there is no locationId, so keying on it always showed
      // "Not connected". Key on merchantId (mirrors MobileSettings).
      const q = get('square');
      if (q.merchantId || q.connected) {
        const meta = 'Square POS · ' + (q.businessName || q.merchantName || q.merchantId || 'connected');
        return { connected: true, label: 'Online', variant: 'success', meta };
      }
      return NOT_CONNECTED;
    }
    case 'fourthwall': {
      /* fourthwall 2026-07-18, Basic-auth credential pair saved via
         POST /api/fourthwall/connect. The password (apiKey) is encrypted at
         rest and stripped before the browser (SENSITIVE_TOKEN_FIELDS), so key
         on connected/apiUser, mirrors square.merchantId. */
      const fw = get('fourthwall');
      if (fw.connected || fw.apiUser) {
        return { connected: true, label: 'Online', variant: 'success', meta: 'Fourthwall · ' + String(fw.shop || 'online store') };
      }
      return NOT_CONNECTED;
    }
    case 'tiktok': {
      const t = get('tiktok');
      if (t.id || t.openId || t.username) {
        const label = t.username
          ? '@' + t.username
          : t.displayName
            ? String(t.displayName)
            : t.id
              ? String(t.id)
              : 'TikTok account linked';
        return { connected: true, label: 'Connected', variant: 'success', meta: label };
      }
      return NOT_CONNECTED;
    }
    case 'mailchimp': {
      const mc = get('mailchimp');
      if (mc.audienceId || mc.listId || mc.connected) {
        const label = (mc.audienceName || mc.listName || mc.accountName || '') as string;
        return { connected: true, label: 'Connected', variant: 'success', meta: label };
      }
      return NOT_CONNECTED;
    }
    case 'bandsintown': {
      const b = get('bandsintown');
      return b.artist
        ? { connected: true, label: 'Online', variant: 'success', meta: String(b.artist) }
        : NOT_CONNECTED;
    }
    default:
      return NOT_CONNECTED;
  }
}

/** OAuth/redirect connect URLs (connectors-wiring.js metaConnect/shopify/square/…). */
export function oauthConnectUrl(platform: string, extra?: string): string {
  const t = getToken();
  const tq = t ? '?token=' + encodeURIComponent(t) : '';
  switch (platform) {
    case 'meta':
      return t ? AUTH_WORKER + '/api/meta/auth?token=' + encodeURIComponent(t) : AUTH_WORKER + '/api/auth/facebook/start?mode=login';
    case 'square':
      return AUTH_WORKER + '/api/square/auth' + tq;
    case 'tiktok':
      return AUTH_WORKER + '/api/tiktok/auth' + tq;
    case 'mailchimp':
      return AUTH_WORKER + '/api/mailchimp/auth' + tq;
    case 'shopify': {
      // extra = myshopify domain
      let qs = '?shop=' + encodeURIComponent(extra || '');
      if (t) qs += '&auth_token=' + encodeURIComponent(t);
      return AUTH_WORKER + '/api/shopify/auth' + qs;
    }
    case 'fourthwall': {
      /* fourthwall-oauth 2026-07-18, extra = the band's Fourthwall shop
         (slug or full host; the worker normalizes). */
      let fq = '?shop=' + encodeURIComponent(extra || '');
      if (t) fq += '&token=' + encodeURIComponent(t);
      return AUTH_WORKER + '/api/fourthwall/auth' + fq;
    }
    default:
      return '';
  }
}

/**
 * Finalize an OAuth return that landed on /app?<provider>_connected=1.
 *
 * Meta is special: the callback redirects with a short-lived `exchange_code`
 * (the access token is NEVER in the URL), the client MUST POST it to
 * /api/meta/exchange or the worker never writes the token to D1 and the
 * connection silently fails (stats come back has_meta:false). Legacy
 * connectors-wiring.js did this on every /app load; the React app must too,
 * before the flip this ran on legacy /app, after the flip /app is React, so
 * without this Meta reconnect appears to "do nothing".
 *
 * Other providers (Shopify/Square/TikTok) save the token server-side inside
 * their callback, so they only need the URL stripped. Returns true if a Meta
 * exchange succeeded (caller can refresh connections).
 */
export async function finalizeOAuthReturn(): Promise<boolean> {
  try {
    const params = new URLSearchParams(window.location.search);
    const isMeta = params.get('meta_connected') === '1' || params.get('fb_login') === '1';
    const code = params.get('exchange_code') || '';
    const hadReturn =
      isMeta ||
      params.has('shopify_connected') || params.has('shopify_error') ||
      params.has('square_connected') || params.has('square_error') ||
      params.has('fourthwall_connected') || params.has('fourthwall_error') ||
      params.has('tiktok_connected') || params.has('tiktok_error');
    if (!hadReturn) return false;

    let metaFailed = false;
    if (isMeta && code) {
      try {
        const res = await fetch(AUTH_WORKER + '/api/meta/exchange', {
          method: 'POST',
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });
        metaFailed = !res.ok;
        if (!res.ok) {
          try { (window as unknown as { __lastMetaExchangeError?: unknown }).__lastMetaExchangeError = await res.json(); } catch { /* noop */ }
        }
      } catch { metaFailed = true; }
    }

    /* oauth-error-surface 2026-07-18 (Ryan), a failed OAuth return used to be
       stripped + reloaded with ZERO feedback (today's find: a dead Shopify
       client secret produced shopify_error=token_exchange_failed on every
       attempt and the UI ate it). Stash the error BEFORE the strip below;
       App.tsx renders it as a dismissible banner via takeOAuthReturnError(). */
    let errorProvider = params.has('shopify_error') ? 'Shopify'
      : params.has('square_error') ? 'Square'
      : params.has('fourthwall_error') ? 'Fourthwall'
      : params.has('tiktok_error') ? 'TikTok' : '';
    let errorCode = params.get('shopify_error') || params.get('square_error') || params.get('fourthwall_error') || params.get('tiktok_error') || '';
    if (!errorProvider && isMeta && metaFailed) { errorProvider = 'Meta'; errorCode = 'exchange_failed'; }
    if (errorProvider) {
      try {
        sessionStorage.setItem(OAUTH_RETURN_ERROR_KEY, JSON.stringify({ provider: errorProvider, code: errorCode || 'unknown', at: Date.now() }));
      } catch { /* storage unavailable, the error just won't surface */ }
    }

    /* shopify-claim 2026-07-13, THE COLD APP-STORE INSTALL PATH (launch
       blocker caught in a live install test). The worker lands the merchant
       on /app?shopify_connected=1&shopify_exchange_code=… and that code is
       the ONLY link between the installed store and whatever AC account
       signs in next, the OAuth callback couldn't save server-side because
       there was no session (initUser null). Before this block, the code was
       stripped with the URL below and never POSTed, so a cold merchant's
       store NEVER attached ("installed but not connected", the May
       App-Review rejection pattern, aimed at real merchants). Signed in →
       claim NOW (the worker's consume also stamps billing_source='shopify'
       onto accounts with no billing identity, per SHOPIFY-BILLING-PLAN.md).
       Signed out → stash the code (server TTL is 7 days) so
       claimPendingShopifyExchange() can POST it right after sign-in. */
    const shopifyCode = params.get('shopify_exchange_code') || '';
    if (shopifyCode) {
      let claimed = false;
      if (getToken()) {
        try {
          const res = await fetch(AUTH_WORKER + '/api/shopify/exchange', {
            method: 'POST',
            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ code: shopifyCode }),
          });
          claimed = res.ok;
        } catch { /* network hiccup, the stash below keeps the code claimable */ }
      }
      if (!claimed) {
        try {
          localStorage.setItem(PENDING_SHOPIFY_KEY, JSON.stringify({
            code: shopifyCode, shop: params.get('shop') || '', at: Date.now(),
          }));
        } catch { /* storage unavailable, merchant can reconnect from Settings */ }
      }
    }

    // Strip provider params (exchange_code must not sit in history; path kept so a
    // deep-linked /app/<tab> stays put) + drop the connections cache so the
    // post-reload fetch reflects the new connection.
    try { history.replaceState({}, document.title, window.location.pathname); } catch { /* noop */ }
    try { localStorage.removeItem('ac:cache:connections'); } catch { /* noop */ }

    // Reload so Settings/dashboard re-read the fresh connection without a manual
    // refresh, for ANY provider return, except a failed Meta exchange or an
    // error return (nothing new to show; staying put lets the banner render).
    return !(isMeta && metaFailed) && !errorProvider;
  } catch { /* noop */ }
  return false;
}

/* shopify-claim 2026-07-13: where a cold-install exchange code waits for a
   session. Written by finalizeOAuthReturn (signed-out landing), consumed by
   claimPendingShopifyExchange (first authed boot after sign-in/signup). */
const PENDING_SHOPIFY_KEY = 'ac:pending:shopify-exchange';

/* oauth-error-surface 2026-07-18: where a failed OAuth return waits for the
   post-strip render. Written by finalizeOAuthReturn, consumed once by App.tsx. */
const OAUTH_RETURN_ERROR_KEY = 'ac:oauth-return-error';

/** One-shot read of a stashed OAuth-return error. Self-clears, and a stash
    older than 2 minutes is dropped so it can never surface on a later boot. */
export function takeOAuthReturnError(): { provider: string; code: string } | null {
  try {
    const raw = sessionStorage.getItem(OAUTH_RETURN_ERROR_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(OAUTH_RETURN_ERROR_KEY);
    const p = JSON.parse(raw) as { provider?: string; code?: string; at?: number };
    if (!p.provider || Date.now() - (p.at || 0) > 120000) return null;
    return { provider: p.provider, code: p.code || 'unknown' };
  } catch { return null; }
}

/** Claim a stashed cold-install Shopify exchange code once a session exists.
    Returns true when a claim succeeded (caller should reload so the dashboard
    + billing gate reflect the new connection). Self-clears on success AND on
    a burned/expired code (server 400) so it never retries forever; a network
    failure keeps the stash for the next boot (server TTL 7 days). */
export async function claimPendingShopifyExchange(): Promise<boolean> {
  try {
    const raw = localStorage.getItem(PENDING_SHOPIFY_KEY);
    if (!raw || !getToken()) return false;
    let pending: { code?: string; at?: number } = {};
    try { pending = JSON.parse(raw) as { code?: string; at?: number }; } catch { /* corrupt */ }
    if (!pending.code || Date.now() - (pending.at || 0) > 6.5 * 86400000) {
      localStorage.removeItem(PENDING_SHOPIFY_KEY);
      return false;
    }
    const res = await fetch(AUTH_WORKER + '/api/shopify/exchange', {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: pending.code }),
    });
    if (res.ok || res.status === 400) localStorage.removeItem(PENDING_SHOPIFY_KEY);
    if (res.ok) { try { localStorage.removeItem('ac:cache:connections'); } catch { /* noop */ } }
    return res.ok;
  } catch { /* keep the stash, retry on next boot */ }
  return false;
}

/** GET STATS /spotify-search?q= → artist list (spotifyConnect search path). */
export interface SpotifySearchHit {
  id: string;
  name: string;
  followers?: number;
}
export async function spotifySearch(q: string): Promise<SpotifySearchHit[]> {
  try {
    const r = await fetch(STATS_WORKER + '/spotify-search?q=' + encodeURIComponent(q), {
      headers: { Authorization: 'Bearer ' + getToken() },
    });
    const data = (await r.json()) as { artists?: SpotifySearchHit[]; results?: SpotifySearchHit[] };
    return (data && (data.artists || data.results)) || [];
  } catch {
    return [];
  }
}

/** GET /api/bandsintown/artist?artist= → { artist:{name,trackerCount} } (bitConnect). */
export async function bandsintownLookup(
  slug: string,
): Promise<{ name: string; trackerCount: number } | null> {
  try {
    const r = await fetch(AUTH_WORKER + '/api/bandsintown/artist?artist=' + encodeURIComponent(slug), {
      headers: { Authorization: 'Bearer ' + getToken() },
    });
    const data = (await r.json()) as { artist?: { name?: string; trackerCount?: number } };
    if (!data || !data.artist || !data.artist.name) return null;
    return { name: data.artist.name, trackerCount: data.artist.trackerCount || 0 };
  } catch {
    return null;
  }
}

// ─── Bandsintown dashboard login (push session, connectors-wiring.js bitDash*) ─

export interface BitDashStatus {
  connected: boolean;
  via?: string;
  who?: string;
}

/** GET /api/bandsintown/dash-status → { connected, via/method, email/account }. */
export async function bitDashStatus(): Promise<BitDashStatus> {
  if (!getToken()) return { connected: false };
  try {
    const r = await fetch(AUTH_WORKER + '/api/bandsintown/dash-status', {
      headers: { Authorization: 'Bearer ' + getToken() },
    });
    const d = (await r.json()) as {
      connected?: boolean;
      via?: string;
      method?: string;
      email?: string;
      account?: string;
    };
    return { connected: !!d.connected, via: d.via || d.method, who: d.email || d.account };
  } catch {
    return { connected: false };
  }
}

/** POST /api/bandsintown/store-credentials { auth_type:'email', email, password }. */
export async function bitDashLogin(
  email: string,
  password: string,
): Promise<{ ok: boolean; message: string }> {
  if (!getToken()) return { ok: false, message: "You're not logged in." };
  try {
    const r = await fetch(AUTH_WORKER + '/api/bandsintown/store-credentials', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + getToken() },
      body: JSON.stringify({ auth_type: 'email', email, password }),
    });
    const d = (await r.json()) as { success?: boolean; message?: string; error?: string };
    return d && d.success
      ? { ok: true, message: 'Connected, push is ready.' }
      : { ok: false, message: (d && (d.message || d.error)) || 'Login failed, check your email and password.' };
  } catch {
    return { ok: false, message: 'Connection error, try again.' };
  }
}

/** POST /api/bandsintown/disconnect-dash. */
export async function bitDashDisconnect(): Promise<void> {
  if (!getToken()) return;
  try {
    await fetch(AUTH_WORKER + '/api/bandsintown/disconnect-dash', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + getToken() },
    });
  } catch {
    /* ignore */
  }
}

/** Facebook OAuth dialog URL for the BIT push session (bitFacebookConnect). */
export function bitFacebookUrl(): string {
  const t = getToken();
  const redirectUri = AUTH_WORKER + '/api/bandsintown/fb-callback';
  const state = t ? 'bit_connect:' + t : 'bit_connect';
  return (
    'https://www.facebook.com/v18.0/dialog/oauth?client_id=1644971083348042&redirect_uri=' +
    encodeURIComponent(redirectUri) +
    '&scope=pages_show_list&state=' +
    encodeURIComponent(state) +
    '&response_type=code'
  );
}

// ─── Google Calendar sync (settings-wiring.js wireCalendarSync) ────────────────

export type GcalState = 'loading' | 'disconnected' | 'connected';

export async function gcalStatus(): Promise<{ connected: boolean; email?: string }> {
  if (!getToken()) return { connected: false };
  try {
    const r = await fetch(AUTH_WORKER + '/api/google-calendar/status', {
      headers: { Authorization: 'Bearer ' + getToken() },
    });
    if (!r.ok) return { connected: false };
    const j = (await r.json()) as { connected?: boolean; email?: string };
    return { connected: !!j.connected, email: j.email };
  } catch {
    return { connected: false };
  }
}

export async function gcalAuthUrl(): Promise<string | null> {
  if (!getToken()) return null;
  try {
    const r = await fetch(AUTH_WORKER + '/api/google-calendar/auth', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + getToken() },
    });
    if (!r.ok) return null;
    const j = (await r.json()) as { authUrl?: string };
    return j.authUrl || null;
  } catch {
    return null;
  }
}

export async function gcalSync(): Promise<{ ok: boolean; created?: number; updated?: number }> {
  if (!getToken()) return { ok: false };
  try {
    const r = await fetch(AUTH_WORKER + '/api/google-calendar/sync', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + getToken() },
    });
    if (!r.ok) return { ok: false };
    const j = (await r.json()) as { success?: boolean; created?: number; updated?: number };
    return { ok: !!j.success, created: j.created, updated: j.updated };
  } catch {
    return { ok: false };
  }
}

export async function gcalDisconnect(): Promise<void> {
  if (!getToken()) return;
  try {
    await fetch(AUTH_WORKER + '/api/google-calendar/disconnect', {
      method: 'POST',
      headers: { Authorization: 'Bearer ' + getToken() },
    });
  } catch {
    /* ignore */
  }
}

/** Open an OAuth popup centered on screen (gcal connect). */
export function openOAuthPopup(url: string, name: string, w = 520, h = 640): Window | null {
  const left = Math.max(0, (window.innerWidth - w) / 2);
  const top = Math.max(0, (window.innerHeight - h) / 2);
  return window.open(
    url,
    name,
    'width=' + w + ',height=' + h + ',left=' + left + ',top=' + top + ',toolbar=0,menubar=0',
  );
}
